import { useEffect, useState, type FormEvent } from 'react';
import '../App.css';
import type { InvestmentCase, InvestmentCaseFounder } from '../types';
import { GlobalButton } from './GlobalButton';
import { GlobalInput } from './GlobalInput';
import { GlobalTextarea } from './GlobalTextarea';
import GlobalTabs from './GlobalTabs';
import { normalizeWebsiteUrl } from '../utils/favicon';
import { clampRating, founderForStorage, foundersForStorage } from '../utils/investmentCase';
import { normalizeCountryCode } from '../utils/investmentCompanyUi';
import { newId } from '../utils/id';
import {
  extractDeckText,
  extractDeckTextErrorMessage,
} from '../lib/extractDeckText';
import {
  deleteInvestmentCaseDeck,
  uploadInvestmentCaseDeck,
} from '../lib/investmentCaseDeckStorage';

type Props = {
  investmentCase?: InvestmentCase | null;
  userId: string;
  onSubmit: (next: InvestmentCase) => void;
  onCancel: () => void;
  onDelete?: () => void;
};

type CaseDraft = Omit<InvestmentCase, 'id' | 'createdAt' | 'updatedAt'>;

function emptyDraft(): CaseDraft {
  return {
    name: '',
    industry: '',
    founderAmbition: 3,
    timeToRevenue: 3,
    exitExperience: 3,
    founderSkillset: 3,
    marketSize: 3,
    founders: [],
  };
}

function normalizeFounders(founders: InvestmentCaseFounder[] | undefined): InvestmentCaseFounder[] {
  if (!Array.isArray(founders)) return [];
  return founders
    .map((f) =>
      founderForStorage({
        id: typeof f.id === 'string' && f.id.trim() ? f.id : newId(),
        name: typeof f.name === 'string' ? f.name : undefined,
        linkedinUrl: typeof f.linkedinUrl === 'string' ? f.linkedinUrl : undefined,
        profileText: typeof f.profileText === 'string' ? f.profileText : undefined,
      })
    )
    .filter((f): f is InvestmentCaseFounder => f !== null);
}

function normalizeDraft(c: InvestmentCase): CaseDraft {
  const { id: _id, createdAt: _c, updatedAt: _u, ...rest } = c;
  return {
    ...rest,
    founderAmbition: clampRating(rest.founderAmbition),
    timeToRevenue: clampRating(rest.timeToRevenue),
    exitExperience: clampRating(rest.exitExperience),
    founderSkillset: clampRating(rest.founderSkillset),
    marketSize: clampRating(rest.marketSize),
    founders: normalizeFounders(rest.founders),
    deckOcrText: typeof rest.deckOcrText === 'string' ? rest.deckOcrText : undefined,
    deckPdfUrl: typeof rest.deckPdfUrl === 'string' ? rest.deckPdfUrl : undefined,
    deckPdfStoragePath:
      typeof rest.deckPdfStoragePath === 'string' ? rest.deckPdfStoragePath : undefined,
    deckPdfFileName: typeof rest.deckPdfFileName === 'string' ? rest.deckPdfFileName : undefined,
  };
}

function foundersForSave(founders: InvestmentCaseFounder[]): InvestmentCaseFounder[] | undefined {
  return foundersForStorage(founders);
}

const RATING_FIELDS: Array<{ key: keyof Pick<
  CaseDraft,
  'founderAmbition' | 'timeToRevenue' | 'exitExperience' | 'founderSkillset' | 'marketSize'
>; label: string }> = [
  { key: 'founderAmbition', label: 'Founder ambition' },
  { key: 'timeToRevenue', label: 'Time to revenue' },
  { key: 'exitExperience', label: 'Exit experience' },
  { key: 'founderSkillset', label: 'Founder skillset' },
  { key: 'marketSize', label: 'Market size' },
];

export function InvestmentCaseForm({ investmentCase, userId, onSubmit, onCancel, onDelete }: Props) {
  const [draft, setDraft] = useState<CaseDraft>(() =>
    investmentCase ? normalizeDraft(investmentCase) : emptyDraft()
  );
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [removePdf, setRemovePdf] = useState(false);
  const [ocrEditedAfterFilePick, setOcrEditedAfterFilePick] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(investmentCase ? normalizeDraft(investmentCase) : emptyDraft());
    setPdfFile(null);
    setRemovePdf(false);
    setOcrEditedAfterFilePick(false);
    setFormError(null);
  }, [investmentCase]);

  function updateFounder(id: string, patch: Partial<InvestmentCaseFounder>) {
    setDraft((prev) => ({
      ...prev,
      founders: (prev.founders ?? []).map((f) => (f.id === id ? { ...f, ...patch } : f)),
    }));
  }

  function addFounder() {
    setDraft((prev) => ({
      ...prev,
      founders: [...(prev.founders ?? []), { id: newId(), name: '', linkedinUrl: '', profileText: '' }],
    }));
  }

  function removeFounder(id: string) {
    setDraft((prev) => ({
      ...prev,
      founders: (prev.founders ?? []).filter((f) => f.id !== id),
    }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const now = new Date().toISOString();
    const id = investmentCase?.id?.trim() ? investmentCase.id : newId();
    const base = investmentCase
      ? investmentCase
      : ({ id, createdAt: now, updatedAt: now } as InvestmentCase);

    const name = draft.name.trim();
    if (!name) {
      setFormError('Add a company name.');
      return;
    }

    if (!userId) {
      setFormError('You must be signed in to save.');
      return;
    }

    setFormError(null);
    setSaving(true);

    try {
      if (removePdf && investmentCase?.deckPdfStoragePath) {
        await deleteInvestmentCaseDeck(investmentCase.deckPdfStoragePath);
      }

      let deckPdfUrl: string | undefined = removePdf ? undefined : investmentCase?.deckPdfUrl;
      let deckPdfStoragePath: string | undefined = removePdf
        ? undefined
        : investmentCase?.deckPdfStoragePath;
      let deckPdfFileName: string | undefined = removePdf
        ? undefined
        : investmentCase?.deckPdfFileName;

      let uploadedNewPdf = false;
      if (pdfFile) {
        if (investmentCase?.deckPdfStoragePath && !removePdf) {
          await deleteInvestmentCaseDeck(investmentCase.deckPdfStoragePath);
        }
        const uploaded = await uploadInvestmentCaseDeck(userId, id, pdfFile);
        deckPdfUrl = uploaded.deckPdfUrl;
        deckPdfStoragePath = uploaded.deckPdfStoragePath;
        deckPdfFileName = pdfFile.name;
        uploadedNewPdf = true;
      }

      const country = normalizeCountryCode(
        typeof draft.countryCode === 'string' ? draft.countryCode : String(draft.countryCode ?? '')
      );

      const fy =
        draft.foundingYear === undefined || draft.foundingYear === null
          ? undefined
          : Number(draft.foundingYear);

      let deckOcrText = draft.deckOcrText?.trim() ? draft.deckOcrText.trim() : undefined;

      if (uploadedNewPdf && deckPdfStoragePath && !ocrEditedAfterFilePick) {
        try {
          const extracted = await extractDeckText(deckPdfStoragePath);
          deckOcrText = extracted.trim() ? extracted.trim() : undefined;
        } catch (err) {
          setFormError(
            `PDF saved, but text extraction failed: ${extractDeckTextErrorMessage(err)}`
          );
        }
      }

      onSubmit({
        ...base,
        ...draft,
        id,
        name,
        websiteUrl: normalizeWebsiteUrl(
          typeof draft.websiteUrl === 'string' ? draft.websiteUrl : String(draft.websiteUrl ?? '')
        ),
        countryCode: country,
        foundingYear: fy && fy > 1800 && fy < 2100 ? Math.round(fy) : undefined,
        founderAmbition: clampRating(draft.founderAmbition),
        timeToRevenue: clampRating(draft.timeToRevenue),
        exitExperience: clampRating(draft.exitExperience),
        founderSkillset: clampRating(draft.founderSkillset),
        marketSize: clampRating(draft.marketSize),
        deckPdfUrl,
        deckPdfStoragePath,
        deckPdfFileName,
        deckOcrText,
        founders: foundersForSave(draft.founders ?? []),
        updatedAt: now,
        createdAt: investmentCase?.createdAt ?? now,
      });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const hasExistingPdf = Boolean(investmentCase?.deckPdfUrl && !removePdf);
  const showUndoRemove = Boolean(removePdf && investmentCase?.deckPdfUrl);
  const founders = draft.founders ?? [];

  const detailsTab = (
    <div className="sidebarFormStack" style={{ paddingTop: 0 }}>
      <GlobalInput
        label="Name"
        value={draft.name}
        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        required
      />
      <GlobalInput
        label="Website URL"
        value={draft.websiteUrl ?? ''}
        onChange={(e) => setDraft({ ...draft, websiteUrl: e.target.value || undefined })}
        placeholder="https:// or example.com"
      />
      <GlobalInput
        label="Industry"
        value={draft.industry}
        onChange={(e) => setDraft({ ...draft, industry: e.target.value })}
      />
      <GlobalInput
        label="Country (ISO code)"
        value={draft.countryCode ?? ''}
        onChange={(e) =>
          setDraft({
            ...draft,
            countryCode: e.target.value ? e.target.value.toUpperCase().slice(0, 2) : undefined,
          })
        }
        placeholder="e.g. DE"
        maxLength={2}
        autoCapitalize="characters"
      />
      <GlobalInput
        label="Founding year"
        type="number"
        value={draft.foundingYear ?? ''}
        onChange={(e) =>
          setDraft({
            ...draft,
            foundingYear: e.target.value === '' ? undefined : Number(e.target.value),
          })
        }
      />

      {RATING_FIELDS.map(({ key, label }) => (
        <div key={key}>
          <label className="investmentCaseRatingLabel">
            <span>{label}</span>
            <span className="investmentCaseRatingValue">{clampRating(draft[key])}/6</span>
          </label>
          <input
            type="range"
            className="investmentCaseRange"
            min={1}
            max={6}
            step={1}
            value={clampRating(draft[key])}
            onChange={(e) =>
              setDraft({ ...draft, [key]: Number(e.target.value) } as CaseDraft)
            }
          />
        </div>
      ))}
    </div>
  );

  const deckTab = (
    <div className="sidebarFormStack" style={{ paddingTop: 0 }}>
      <div>
        <span className="investmentCaseDeckFileLabel">Pitch deck (PDF)</span>
        <input
          type="file"
          accept="application/pdf,.pdf"
          className="investmentCaseDeckFileInput"
          onChange={(e) => {
            const f = e.target.files?.[0];
            setPdfFile(f ?? null);
            if (f) {
              setRemovePdf(false);
              setOcrEditedAfterFilePick(false);
            }
          }}
        />
        {pdfFile ? (
          <>
            <p className="muted investmentCaseDeckFileMeta">Selected file: {pdfFile.name}</p>
            <p className="muted investmentCaseDeckFileHint">PDF will upload on save.</p>
          </>
        ) : null}
        {hasExistingPdf ? (
          <p className="muted investmentCaseDeckFileMeta">
            Current file: {investmentCase?.deckPdfFileName || 'deck.pdf'}
            {investmentCase?.deckPdfUrl ? (
              <>
                {' '}
                <a href={investmentCase.deckPdfUrl} target="_blank" rel="noopener noreferrer">
                  Open
                </a>
              </>
            ) : null}
          </p>
        ) : null}
        {hasExistingPdf ? (
          <GlobalButton type="button" variant="secondary" onClick={() => setRemovePdf(true)}>
            Remove PDF
          </GlobalButton>
        ) : null}
        {showUndoRemove ? (
          <GlobalButton type="button" variant="secondary" onClick={() => setRemovePdf(false)}>
            Keep PDF
          </GlobalButton>
        ) : null}
        {removePdf && investmentCase?.deckPdfUrl ? (
          <p className="muted investmentCaseDeckFileHint">PDF will be removed when you save.</p>
        ) : null}
      </div>

      <GlobalTextarea
        label="Deck OCR text"
        value={draft.deckOcrText ?? ''}
        onChange={(e) => {
          if (pdfFile) setOcrEditedAfterFilePick(true);
          setDraft({ ...draft, deckOcrText: e.target.value });
        }}
        rows={18}
        placeholder="Paste OCR text from the deck here, or save after uploading a PDF to extract text automatically."
      />
    </div>
  );

  const foundersTab = (
    <div className="sidebarFormStack" style={{ paddingTop: 0 }}>
      {founders.length === 0 ? (
        <p className="muted">No founders yet. Add one to capture name, LinkedIn, and profile notes.</p>
      ) : null}

      {founders.map((founder, index) => (
        <div key={founder.id} className="investmentCaseFounderCard">
          <div className="investmentCaseFounderCardHead">
            <span className="investmentCaseFounderCardTitle">
              {founder.name?.trim() || `Founder ${index + 1}`}
            </span>
            <GlobalButton
              type="button"
              variant="secondary"
              destructive
              onClick={() => removeFounder(founder.id)}
            >
              Remove
            </GlobalButton>
          </div>
          <GlobalInput
            label="Name"
            value={founder.name ?? ''}
            onChange={(e) => updateFounder(founder.id, { name: e.target.value })}
            placeholder="e.g. Jane Doe"
          />
          <GlobalInput
            label="LinkedIn profile URL"
            value={founder.linkedinUrl ?? ''}
            onChange={(e) => updateFounder(founder.id, { linkedinUrl: e.target.value })}
            placeholder="https://linkedin.com/in/…"
          />
          <GlobalTextarea
            label="Profile text"
            value={founder.profileText ?? ''}
            onChange={(e) => updateFounder(founder.id, { profileText: e.target.value })}
            rows={6}
            placeholder="Bio, background, prior exits, notes from LinkedIn or calls…"
          />
        </div>
      ))}

      <GlobalButton type="button" variant="secondary" onClick={addFounder}>
        Add founder
      </GlobalButton>
    </div>
  );

  return (
    <form className="sidebarFormStack" onSubmit={handleSubmit}>
      {formError ? (
        <p className="sidebarFormError" role="alert">
          {formError}
        </p>
      ) : null}

      <GlobalTabs
        tabs={[
          { label: 'Details', content: detailsTab },
          { label: 'Deck', content: deckTab },
          { label: 'Founders', content: foundersTab },
        ]}
      />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'space-between', marginTop: 8 }}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {onDelete ? (
            <GlobalButton type="button" variant="secondary" destructive onClick={onDelete} disabled={saving}>
              Delete
            </GlobalButton>
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <GlobalButton type="button" variant="secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </GlobalButton>
          <GlobalButton type="submit" variant="primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </GlobalButton>
        </div>
      </div>
    </form>
  );
}
